<template>
  <div>
    <slot
        name="prepend"
        v-bind:serviceImplementation="serviceImplementation"
        v-bind:processImplementation="processImplementation"
        v-bind:dataImplementation="dataImplementation">
    </slot>

    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
         サービス実装
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <v-row>
      <template v-if="readonly">
        <v-col cols="6">
          <text-field-like
              :value="serviceImplementation.serviceComponentModel && serviceImplementation.serviceComponentModel.name"
              :value-id="serviceImplementation.id"
              label="サービス実装"
              hide-details>
          </text-field-like>
        </v-col>
        <v-col cols="6">
          <local-id-field
              label="サービス実装 内部ID"
              :value="serviceImplementation.localId"
              hide-details>
          </local-id-field>
        </v-col>
      </template>
      <template v-else>
        <v-col cols="12">
          <v-select
              v-model="selectedServiceImplementationId"
              :items="serviceImplementationItems"
              label="サービス実装"
              :readonly="readonly"
              hide-details>
            <template #item="{item, attrs, on}">
              <v-list-item v-bind="attrs" v-on="on" class="show-label-on-hover">
                <v-col class="name">
                  <menu-inline>
                    <text-field-like
                        label="サービス構成モデル名"
                        :value="item.serviceComponentModel.name"
                        :value-id="item.id">
                    </text-field-like>
                  </menu-inline>
                </v-col>
                <v-col>
                  <menu-inline>
                    <local-id-field
                        label="内部ID"
                        :value="item.localId"
                        hide-details>
                    </local-id-field>
                  </menu-inline>
                </v-col>
              </v-list-item>
            </template>
            <template #selection="{item}">
              <div style="display: inline-flex; justify-content: space-between; width: 100%;">
                <text-field-like
                    :value="item.serviceComponentModel.name"
                    :value-id="item.id"
                    hide-label
                    hide-details>
                </text-field-like>
                <div style="font-size: 80%; align-self: center;">内部ID: {{ item.localId }}</div>
              </div>
            </template>
          </v-select>
        </v-col>
      </template>
    </v-row>

    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
         プロセス実装
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <v-row>
      <template v-if="readonly">
        <v-col cols="6">
          <text-field-like
              :value="processImplementation.processComponentModel && processImplementation.processComponentModel.name"
              :value-id="processImplementation.id"
              label="プロセス実装"
              hint="プロセス実装の参照するプロセス構成モデル名"
              hide-details>
          </text-field-like>
        </v-col>
        <v-col cols="6">
          <local-id-field
              label="プロセス実装 内部ID"
              :value="processImplementation.localId"
              hide-details>
          </local-id-field>
        </v-col>
      </template>
      <template v-else>
        <v-col cols="12">
          <v-select
              v-model="selectedProcessImplementationId"
              :items="processImplementationItems"
              label="プロセス実装"
              :readonly="readonly"
              :disabled="!selectedServiceImplementationId"
              :hint="selectedServiceImplementationId ? '' : '先にサービス実装を選択してください'"
              persistent-hint>
            <template #item="{item, attrs, on}">
              <v-list-item v-bind="attrs" v-on="on" class="show-label-on-hover">
                <v-col class="name">
                  <menu-inline>
                    <text-field-like
                        label="プロセス構成モデル名"
                        :value="item.processComponentModel.name"
                        :value-id="item.id">
                    </text-field-like>
                  </menu-inline>
                </v-col>
                <v-col>
                  <menu-inline>
                    <local-id-field
                        label="内部ID"
                        :value="item.localId">
                    </local-id-field>
                  </menu-inline>
                </v-col>
              </v-list-item>
            </template>
            <template #selection="{item}">
              <div style="display: inline-flex; justify-content: space-between; width: 100%;">
                <text-field-like
                    :value="item.processComponentModel.name"
                    :value-id="item.id"
                    hide-label
                    hide-details>
                </text-field-like>
                <div style="font-size: 80%; align-self: center;">内部ID: {{ item.localId }}</div>
              </div>
            </template>
          </v-select>
        </v-col>
      </template>
    </v-row>

    <!-- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
         データ実装
     - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -->
    <v-row>
      <v-col cols="6">
        <text-field-like
            :value="dataImplementation.name"
            :value-id="dataImplementation.id"
            label="データ実装"
            hint="データ実装の参照するデータ構成モデル名"
            hide-details>
        </text-field-like>
      </v-col>
      <v-col cols="6">
        <local-id-field
            :value="dataImplementation.localId"
            label="データ実装 内部ID"
            hide-details>
        </local-id-field>
      </v-col>
    </v-row>

    <slot
        name="append"
        v-bind:serviceImplementation="serviceImplementation"
        v-bind:processImplementation="processImplementation"
        v-bind:dataImplementation="dataImplementation">
    </slot>
  </div>
</template>

<script>
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import LocalIdField from "@/components/molecules/LocalIdField.vue";
import MenuInline from "@/components/atoms/MenuInline.vue";

export default {
  name: 'ProcessImplementationSelectForm',
  components: {MenuInline, LocalIdField, TextFieldLike},
  model: {
    prop: 'processImplementationId',
    event: 'change'
  },
  inject: {
    form: {default: null}
  },
  props: {
    processImplementationId: {
      type: String,
      default: null
    },
    serviceImplementations: {
      type: Array,
      default: () => []
    },
    processImplementations: {
      type: Array,
      default: () => []
    },
    dataImplementation: {
      type: Object,
      default: () => ({})
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedProcessImplementationId: null,
      selectedServiceImplementationId: null,
    }
  },
  created() {
    this.form && this.form.register(this);
  },
  beforeDestroy() {
    this.form && this.form.unregister(this);
  },
  computed: {
    serviceImplementationItems() {
      return this.serviceImplementations.map(si => ({ value: si.id, ...si }));
    },
    serviceImplementation() {
      const serviceImplementationId = this.processImplementation?.serviceImplementationId;
      if(serviceImplementationId) {
        return this.serviceImplementations.find(si => si.id === serviceImplementationId) ?? null;
      } else {
        return null;
      }
    },
    processImplementation() {
      if(this.processImplementationId) {
        return this.processImplementations.find(pi => pi.id === this.processImplementationId) ?? null;
      } else {
        return null;
      }
    },
    processImplementationItems() {
      return this.processImplementations
          .filter(pi => pi.serviceImplementationId === this.selectedServiceImplementationId)
          .map(pi => ({ value: pi.id, ...pi }));
    },
    hasError() {
      return !this.processImplementationId;
    }
  },
  watch: {
    processImplementationId: {
      immediate: true,
      handler(processImplementationId) {
        this.selectedProcessImplementationId = processImplementationId;
      }
    },
    selectedProcessImplementationId: {
      handler(selectedProcessImplementationId) {
        this.$emit('change', selectedProcessImplementationId);
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.col.name {
  max-width: 200px;
}
</style>
